/* eslint-disable no-void */

import React from "react"
import { View, StyleSheet, TouchableOpacity, Platform } from "react-native"
import { type TooltipProps, useCopilot } from "react-native-copilot"
import { BFColors } from "@/constants/Colors"
import { BFButton } from "@/components/buttons/CommonButtons"
import { Tiny, H5, Small } from "@/components/standard/BFText"
import { useViewedCallback } from "@/analytics/react"
import * as AnalyzerEvents from "@/analytics/events/analyzer"
import { setSubjectPropertyWalkthroughDismissed } from "@/stores/walkThroughs"
import { useAnalyzeContext } from "@/components/analysis/AnalyzeContext"
import { extractText } from "@/utils/StringUtils"

const CustomTooltip: React.FC<TooltipProps> = ({ labels }) => {
  const { goToNext, goToPrev, stop, currentStep, isFirstStep, isLastStep } =
    useCopilot()

  const { analysisId } = useAnalyzeContext()

  useViewedCallback(() => {
    AnalyzerEvents.customizeCompsWalkthroughViewed()
  }, [])

  const onContinuePress = () => {
    if (isNewCompSelectionCallout) {
      AnalyzerEvents.customizeCompsWalkthroughClosed()
    } else {
      AnalyzerEvents.compSelectionWalkthroughSkipped()
    }
    setSubjectPropertyWalkthroughDismissed()
    void stop()
  }

  const onDonePress = () => {
    AnalyzerEvents.compSelectionWalkthroughCompleted(analysisId)
    setSubjectPropertyWalkthroughDismissed()
    void stop()
  }

  const isNewCompSelectionCallout: boolean =
    currentStep?.name === "adjust filters"

  const onNextStep = () => {
    if (currentStep && typeof currentStep.order === "number") {
      AnalyzerEvents.compSelectionWalkthroughStep(
        currentStep.order + 1,
        labels.next || "Next",
        extractText(currentStep?.text)
      )
      void goToNext()
    } else {
      console.error("No current step order available for Next step.")
    }
  }

  const onPrevStep = () => {
    if (currentStep && typeof currentStep.order === "number") {
      AnalyzerEvents.compSelectionWalkthroughStep(
        currentStep.order - 1,
        labels.previous || "Previous",
        extractText(currentStep?.text)
      )
      void goToPrev()
    } else {
      console.error("No current step order available for Previous step.")
    }
  }

  const renderFinalButton = () => {
    if (isNewCompSelectionCallout) {
      return (
        <BFButton
          testID={"continue-button"}
          size={"sm"}
          title="Got it!"
          onPress={onContinuePress}
          style={{
            backgroundColor: BFColors.midnightBlue,
            marginLeft: 10,
          }}
        />
      )
    } else {
      return (
        <TouchableOpacity
          testID={"copilot-done-button"}
          hitSlop={10}
          onPress={onDonePress}
        >
          <Small className="text-calm-blue text-[14px] ml-8">Done</Small>
        </TouchableOpacity>
      )
    }
  }

  return (
    <TouchableOpacity
      testID={"continue-area-action"}
      activeOpacity={0.85}
      onPress={onContinuePress}
    >
      <View testID="copilot-tooltip" style={styles.tooltipContainer}>
        {/* eslint-disable-next-line react/jsx-no-undef */}
        <H5 className="text-midnight-blue text-[16px] text-left mb-[2px]">
          {currentStep?.text || "N/A"}
        </H5>
        <View
          className="flex-row justify-between w-full"
          style={styles.buttonContainer}
        >
          {!isNewCompSelectionCallout && (
            <TouchableOpacity
              testID={"copilot-skip-button"}
              hitSlop={10}
              onPress={onContinuePress}
            >
              <Tiny className="text-calm-blue text-[12px]">Skip</Tiny>
            </TouchableOpacity>
          )}

          <View
            style={{
              flexGrow: 1,
              flexShrink: 1,
              minWidth: 10,
              minHeight: 10,
              width: "100%",
            }}
          />

          {!isNewCompSelectionCallout && !isFirstStep && (
            <TouchableOpacity
              testID={"copilot-prev-button"}
              hitSlop={10}
              onPress={onPrevStep}
            >
              <Small className="text-calm-blue text-[14px]">
                {labels.previous || "Prev"}
              </Small>
            </TouchableOpacity>
          )}

          {!isNewCompSelectionCallout && !isLastStep ? (
            <TouchableOpacity
              testID={"copilot-next-button"}
              hitSlop={10}
              onPress={onNextStep}
            >
              <Small className="text-calm-blue text-[14px] ml-8">
                {labels.next || "Next"}
              </Small>
            </TouchableOpacity>
          ) : (
            <View className="flex">{renderFinalButton()}</View>
          )}
        </View>
      </View>
    </TouchableOpacity>
  )
}

const styles = StyleSheet.create({
  tooltipContainer: {
    width: 240,
    paddingTop: 6,
    backgroundColor: BFColors.lightest,
    textAlign: "left",
    marginTop: Platform.select({ ios: 0, android: 0 }),
  },
  buttonContainer: {
    alignSelf: "flex-end", // Aligns the button within the container
    marginVertical: 12,
  },
})

export default CustomTooltip
